/**
 * Evaluates whether the current user holds any assignment granting the requested permission.
 * Used to show/hide action buttons without a server round-trip.
 * Returns true when no assignments exist (pre-seeding) to preserve current behavior.
 * BU admins have full access to campaigns/assets within their BU.
 */
import { useMyPermissions } from './useMyPermissions';

type Permission =
  | 'VIEW_CAMPAIGNS'
  | 'MANAGE_CAMPAIGNS'
  | 'APPROVE_ASSETS'
  | 'APPROVE_WORKFLOWS'
  | 'MANAGE_BLOCKLISTS';

export function useCanPerform (permission: Permission): boolean {
  const { isPlatformAdmin, isBusinessUnitAdmin, assignments, isLoading, isError } = useMyPermissions();

  // Loading is transient, so keep controls visible to avoid flicker.
  if (isLoading) return true;

  // An error leaves permissions unknown, so hide permission-gated controls.
  if (isError) return false;
  if (isPlatformAdmin) return true;
  if (isBusinessUnitAdmin) return true;

  // No assignments at all (pre-seeding / no groups exist yet) — allow everything
  // to preserve current behavior until CR E seeds groups
  if (assignments.length === 0) return true;

  return assignments.some(a => a.permissions?.includes(permission));
}
